<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref, useId, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useConfirm, useToast } from "primevue"
import { useForm } from "vee-validate"
import * as yup from "yup"
import { useFetchWrapper } from "@core/composables"
import { UserService } from "@core/services"
import type { Services } from "@services"
import type { CompanyUser } from "@core/services/user.service"
import { ServiceError } from "@core/exceptions"

// ================================
// TYPES & INTERFACES
// ================================

type Props = {
    /**
     * Liste des membres existants (pour vérifier doublons)
     */
    members?: CompanyUser[]
    /**
     * Liste des clients existants (pour vérifier doublons)
     */
    clients?: CompanyUser[]
}

type FormData = {
    firstname: string
    lastname: string
    username: string
    type: "" | "administrateur" | "employe" | "employe_read_only"
}

type RoleOption = {
    label: string
    value: "administrateur" | "employe" | "employe_read_only"
}

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(defineProps<Props>(), {
    members: () => [],
    clients: () => [],
})

const emit = defineEmits<{
    "update:visible": [value: boolean]
    "member:added": []
}>()

const [visible] = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

// ================================
// SERVICES & STORES
// ================================

/**
 * Injection des services
 */
const services = inject<Services>("services")

/**
 * Service utilisateur (fallback si injection ne fonctionne pas)
 */
const userService = new UserService()

/**
 * Toast pour les notifications
 */
const toast = useToast()

/**
 * Dialog de confirmation avant ajout du membre
 */
const confirm = useConfirm()

/**
 * ID du groupe pour BaseConfirmDialog
 */
const componentId = useId()

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: {
                heading1: "Ajouter un membre",
            },
            texts: {
                helper: "Remplissez le formulaire ci-dessous pour ajouter un nouveau membre à votre entreprise.",
                success_message: (ctx: { role?: string }) => {
                    const roleMapping: Record<string, string> = {
                        administrateur: "administrateur",
                        employe: "membre",
                        employe_read_only: "utilisateur",
                    }
                    return `Le ${roleMapping[ctx.role ?? ""] ?? "membre"} a été ajouté avec succès.`
                },
            },
            fields: {
                firstname: {
                    label: "Prénom",
                    sub_label: "Prénom du membre",
                    placeholder: "Prénom",
                },
                lastname: {
                    label: "Nom",
                    sub_label: "Nom du membre",
                    placeholder: "Nom",
                },
                username: {
                    label: "Email",
                    sub_label: "Adresse email du membre",
                    placeholder: "Saisir une adresse email",
                },
                role: {
                    label: "Rôle",
                    sub_label: "Rôle du membre dans l'entreprise",
                    placeholder: "Sélectionner un rôle",
                },
            },
            buttons: {
                add: "Ajouter",
                cancel: "Annuler",
            },
            confirm: {
                title: "Confirmer l'ajout",
                message:
                    "Êtes-vous sûr de vouloir ajouter ce membre à votre entreprise ?",
            },
            validation: {
                firstname_required: "Le prénom est requis",
                lastname_required: "Le nom est requis",
                email_required: "L'email est requis",
                email_invalid: "L'email n'est pas valide",
                role_required: "Le rôle est requis",
            },
            error: {
                already_member:
                    "Cet utilisateur est déjà membre de l'entreprise.",
                restricted_professional:
                    "Les clients particuliers ne peuvent pas être ajoutés comme membres.",
                restricted_role_company:
                    "Ce rôle n'est pas autorisé pour cet utilisateur.",
                unknown: "Une erreur est survenue lors de l'ajout du membre.",
            },
        },
    },
})

// ================================
// DATA
// ================================

/**
 * Message d'erreur API
 */
const errorMessage = ref<string>("")

/**
 * Options de rôle disponibles
 */
const roleOptions: RoleOption[] = [
    {
        label: "Admin",
        value: "administrateur",
    },
    {
        label: "Membre",
        value: "employe",
    },
    {
        label: "Utilisateur",
        value: "employe_read_only",
    },
]

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Regex pour valider l'email
 */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Options de rôle disponibles selon le contexte
 */
const getRoleOptions = computed(() => {
    // Si l'utilisateur est déjà client, limiter à employe_read_only
    const usernameValue = modelUsername.value?.trim().toLowerCase() || ""
    const isAlreadyClient = props.clients.some(
        (client) => client.username.toLowerCase() === usernameValue
    )
    if (isAlreadyClient) {
        return roleOptions.filter(
            (option) => option.value === "employe_read_only"
        )
    }
    return roleOptions
})

// ================================
// FORM VALIDATION (vee-validate)
// ================================

/**
 * Schéma de validation du formulaire avec vee-validate et yup
 */
const schema = yup.object({
    firstname: yup
        .string()
        .required(t("validation.firstname_required"))
        .trim()
        .test("not-empty", t("validation.firstname_required"), (value) => {
            return value !== undefined && value.trim() !== ""
        }),
    lastname: yup
        .string()
        .required(t("validation.lastname_required"))
        .trim()
        .test("not-empty", t("validation.lastname_required"), (value) => {
            return value !== undefined && value.trim() !== ""
        }),
    username: yup
        .string()
        .required(t("validation.email_required"))
        .trim()
        .test("email-format", t("validation.email_invalid"), (value) => {
            if (!value) return false
            return EMAIL_REGEX.test(value.trim())
        })
        .test("not-already-member", t("error.already_member"), (value) => {
            if (!value) return true
            const trimmedEmail = value.trim().toLowerCase()
            return !props.members.some(
                (member) => member.username.toLowerCase() === trimmedEmail
            )
        })
        .test(
            "not-client-particulier",
            t("error.restricted_professional"),
            (value) => {
                if (!value) return true
                const trimmedEmail = value.trim().toLowerCase()
                return !props.clients.some(
                    (client) =>
                        client.username.toLowerCase() === trimmedEmail &&
                        !client?.company
                )
            }
        ),
    type: yup
        .string()
        .required(t("validation.role_required"))
        .oneOf(
            ["administrateur", "employe", "employe_read_only"],
            t("validation.role_required")
        )
        .test(
            "restricted-role-company",
            t("error.restricted_role_company"),
            function (value) {
                if (!value) return true
                const usernameValue =
                    this.parent.username?.trim().toLowerCase() || ""
                const isAlreadyClient = props.clients.some(
                    (client) => client.username.toLowerCase() === usernameValue
                )
                if (isAlreadyClient && value !== "employe_read_only") {
                    return false
                }
                return true
            }
        ),
})

const { errors, defineField, handleSubmit, meta, resetForm } =
    useForm<FormData>({
        validationSchema: schema,
        initialValues: {
            firstname: "",
            lastname: "",
            username: "",
            type: "",
        },
    })

const [modelFirstname] = defineField("firstname")
const [modelLastname] = defineField("lastname")
const [modelUsername] = defineField("username")
const [modelType] = defineField("type")

// ================================
// FETCH WRAPPER
// ================================

/**
 * Wrapper pour l'ajout d'un membre
 */
const {
    fetch: addMember,
    isFetching,
    hasError: _hasError,
    error: _error,
} = useFetchWrapper(
    async (formData: FormData) => {
        errorMessage.value = ""

        if (services?.$addCompanyUser) {
            await services.$addCompanyUser({
                firstname: formData.firstname.trim(),
                lastname: formData.lastname.trim(),
                username: formData.username.trim(),
                type: formData.type as
                    | "administrateur"
                    | "employe"
                    | "employe_read_only",
            })
        } else {
            await userService.addCompanyUser({
                firstname: formData.firstname.trim(),
                lastname: formData.lastname.trim(),
                username: formData.username.trim(),
                type: formData.type as
                    | "administrateur"
                    | "employe"
                    | "employe_read_only",
            })
        }

        toast.add({
            severity: "success",
            summary: t("common.success"),
            detail: t("texts.success_message", {
                role: formData.type,
            }),
            life: 3000,
        })

        emit("member:added")
        onCancel()
    },
    (err: Error) => {
        if (err instanceof ServiceError) {
            const serviceError = err as ServiceError
            switch (serviceError.code) {
                case "already_exist_user":
                    errorMessage.value = t("error.already_member")
                    break
                case "restricted_professional":
                    errorMessage.value = t("error.restricted_professional")
                    break
                case "restricted_role_company":
                    errorMessage.value = t("error.restricted_role_company")
                    break
                default:
                    errorMessage.value =
                        serviceError.message || t("error.unknown")
            }
            if (
                serviceError.code === "unknown" ||
                ![
                    "already_exist_user",
                    "restricted_professional",
                    "restricted_role_company",
                ].includes(serviceError.code)
            ) {
                console.error(
                    "[CompanyAddMemberDialog] Erreur lors de l'ajout du membre:",
                    {
                        code: serviceError.code,
                        message: serviceError.message,
                        status: serviceError.status,
                    }
                )
            }
        } else {
            errorMessage.value = t("error.unknown")
            console.error("[CompanyAddMemberDialog] Erreur inattendue:", err)
        }
    }
)

// ================================
// METHODS
// ================================

/**
 * Gère la soumission : affiche la confirmation puis appelle l'API à l'acceptation
 */
const onSubmit = handleSubmit(
    (formData) => {
        confirm.require({
            group: componentId,
            message: t("confirm.message"),
            header: t("confirm.title"),
            icon: "pi pi-question-circle",
            rejectLabel: t("buttons.cancel"),
            acceptLabel: t("buttons.add"),
            accept: async () => {
                await addMember(formData)
            },
        })
    },
    () => {
        // Erreur de validation gérée par vee-validate
    }
)

/**
 * Gère l'annulation / fermeture du dialog
 */
function onCancel() {
    resetForm()
    errorMessage.value = ""
    visible.value = false
}

/**
 * Gère la mise à jour de la visibilité
 */
function onUpdateVisible(value: boolean) {
    if (!value) {
        onCancel()
    }
}

// ================================
// WATCHERS
// ================================

watch([modelFirstname, modelLastname, modelUsername, modelType], () => {
    errorMessage.value = ""
})

watch(visible, (newVal) => {
    if (!newVal) {
        onCancel()
    }
})
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :closable="true"
        dismissable-mask
        class="company-add-member-dialog max-w-md w-full"
        @update:visible="onUpdateVisible"
    >
        <template #header>
            <h2 class="text-lg text-primary font-semibold">
                {{ t("title.heading1") }}
            </h2>
        </template>

        <div class="flex flex-col gap-4">
            <p class="text-sm text-surface-600 text-center">
                {{ t("texts.helper") }}
            </p>

            <form class="flex flex-col gap-4" @submit.prevent="onSubmit">
                <BaseInputWrapper
                    input-id="firstname"
                    :label="t('fields.firstname.label')"
                    :error="errors.firstname"
                    unfloat
                >
                    <BaseInputText
                        id="firstname"
                        v-model="modelFirstname"
                        :placeholder="t('fields.firstname.placeholder')"
                        class="w-full"
                        :invalid="!!errors.firstname"
                    />
                </BaseInputWrapper>

                <BaseInputWrapper
                    input-id="lastname"
                    :label="t('fields.lastname.label')"
                    :error="errors.lastname"
                    unfloat
                >
                    <BaseInputText
                        id="lastname"
                        v-model="modelLastname"
                        :placeholder="t('fields.lastname.placeholder')"
                        class="w-full"
                        :invalid="!!errors.lastname"
                    />
                </BaseInputWrapper>

                <BaseInputWrapper
                    input-id="username"
                    :label="t('fields.username.label')"
                    :error="errors.username"
                    unfloat
                >
                    <BaseInputText
                        id="username"
                        v-model="modelUsername"
                        type="email"
                        :placeholder="t('fields.username.placeholder')"
                        class="w-full"
                        :invalid="!!errors.username"
                    />
                </BaseInputWrapper>

                <BaseInputWrapper
                    input-id="role"
                    :label="t('fields.role.label')"
                    :error="errors.type"
                    unfloat
                >
                    <BaseSelect
                        id="role"
                        v-model="modelType"
                        :options="getRoleOptions"
                        option-label="label"
                        option-value="value"
                        :placeholder="t('fields.role.placeholder')"
                        class="w-full"
                        :invalid="!!errors.type"
                    />
                </BaseInputWrapper>
            </form>

            <BaseMessage v-if="errorMessage" severity="error" class="mt-2">
                {{ errorMessage }}
            </BaseMessage>
        </div>

        <template #footer>
            <div class="flex justify-end gap-2 w-full">
                <BaseButton
                    :label="t('buttons.cancel')"
                    text
                    severity="secondary"
                    :disabled="isFetching"
                    @click="onCancel"
                />
                <BaseButton
                    :label="t('buttons.add')"
                    severity="primary"
                    :loading="isFetching"
                    :disabled="!meta.valid || isFetching"
                    @click="onSubmit"
                />
            </div>
        </template>
    </BaseDialog>
    <BaseConfirmDialog :group="componentId" />
</template>
